import React, { useMemo } from 'react'
import styled from 'styled-components'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import Divider from '@jarvis.oddle/core/components/Divider'
import { P, H4, H3, SmallText } from '@jarvis.oddle/core/components/Typo'
import {
  colorConstants,
  margin,
  marginSizes,
  paddingSizes,
  borderRadiusSizes
} from '@jarvis.oddle/core/theme/default'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import IconInformation from '@jarvis.oddle/core/components/Icons/IconInformation'
import { analyseStoreOrder } from 'src/utils/order'
import { isEmpty } from 'src/utils/string'
import { useAppContext } from 'src/containers/AppContext'
import { usePopperTooltip } from 'react-popper-tooltip'

const PayableContainer = styled(Div).attrs(() => ({
  padding: paddingSizes.small,
  borderRadius: borderRadiusSizes.medium
}))`
  border: 1px solid ${(props) => props.theme.colorLinesAndBorders};
`

const RowContainer = styled(Flex).attrs(() => ({
  justifyContent: 'space-between'
}))`
  width: 100%;
  color: ${(props) => (props.color ? props.color : colorConstants.defaultText)};
  ${(props) => (props.normalised ? `margin: 0;` : '')}
`

const RowText = styled(P)`
  margin: 0;
`

const RowHeader = styled(H4)`
  margin: ${margin[marginSizes.small]} 0;
`

const PayableHeader = styled(H3)`
  margin: ${margin[marginSizes.small]} 0;
`

const StyledTooltipContainer = styled('div')`
  --tooltipBackground: #212b36 !important;
  background: #212b36 !important;
  color: white !important;
  border-radius: 4px;
  padding: 1rem 1.5rem;
  width: 90%;
  @media (max-width: 1034px) {
    width: 100%;
  }
`

const StyledDeliveryText = styled(RowText)`
  display: flex;
  align-items: center;
`

const StyledIconInformation = styled(IconInformation)`
  margin-left: ${margin[marginSizes.extraSmall]};
`

interface RowProps {
  leftText?
  rightText?: string
  normalised?: boolean
  bold?: boolean
  color?: string
  Text?: typeof RowText
}

const Row = ({
  leftText,
  rightText,
  normalised,
  bold,
  color,
  Text = RowText
}: RowProps): JSX.Element => (
  <RowContainer normalised={normalised}>
    <Text color={color} bold={bold} paddingRight={paddingSizes.medium}>
      {leftText}
    </Text>
    <Text color={color} bold={bold} whiteSpace='nowrap'>
      {rightText}
    </Text>
  </RowContainer>
)

interface PromotionProps {
  promotion: {
    id: string
    name: string
    amount: number
  }
}

const Promotion = ({ promotion }: PromotionProps): JSX.Element => {
  const { formatMoney } = useAppContext()
  return (
    <Row
      leftText={promotion.name}
      rightText={formatMoney(-1 * promotion.amount)}
    />
  )
}

const Charge = (
  charge: Record<string, Record<string, number>>
): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const { formatMoney } = useAppContext()
  return (
    <Row
      leftText={
        charge.charge.description +
        (charge.charge.isInclusive ? ` (${t('Inclusive')})` : '')
      }
      rightText={formatMoney(charge.charge.amount)}
    />
  )
}

interface TotalProps {
  total: number
  normalised: boolean
}

export const Total = ({ total }: TotalProps): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const { formatMoney } = useAppContext()
  return (
    <Row
      bold
      Text={RowHeader}
      leftText={t('Total')}
      rightText={formatMoney(total)}
    />
  )
}

interface DeliveryFeeProps {
  isAppointmentReady: boolean
  storeOrder: Oms.V4.StoreOrder
  deliveryCost: number
  surchargeDeliveryCost: number
  locationDeliveryCost: number
}

const DeliveryFee = ({
  isAppointmentReady = true,
  storeOrder,
  deliveryCost,
  surchargeDeliveryCost,
  locationDeliveryCost
}: DeliveryFeeProps): JSX.Element => {
  const { formatMoney } = useAppContext()
  const { t } = React.useContext(TranslationContext)
  const {
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible,
  } = usePopperTooltip({
    placement: 'auto'
  });
  const isPickup = React.useMemo(() => {
    let isPickup = storeOrder.isPickup

    // the field received from order payload is isPickup
    // whereas the field received user payload is pickup
    if (typeof isPickup !== 'boolean') {
      isPickup = storeOrder.pickup
    }

    return isPickup
  }, [storeOrder.isPickup, storeOrder.pickup])

  return isAppointmentReady && !isPickup ? (
    <>
      <RowContainer ref={setTriggerRef}>
        <StyledDeliveryText paddingRight={paddingSizes.medium}>
          {t('Delivery fee')}
          {
            surchargeDeliveryCost > 0 && (
              <StyledIconInformation width={14} height={14}/>
            )
          }
        </StyledDeliveryText>
        <RowText whiteSpace='nowrap'>
          {deliveryCost === 0 ? t('FREE') : formatMoney(deliveryCost)}
        </RowText>
      </RowContainer>
      {
        visible && (surchargeDeliveryCost > 0) && (
          <StyledTooltipContainer
            ref={setTooltipRef}
            {...getTooltipProps({ className: 'tooltip-container' })}
          >
            <RowContainer>
              <RowText color={colorConstants.white} paddingRight={paddingSizes.medium}>
                {t('Base Fee')}
              </RowText>
              <RowText color={colorConstants.white} whiteSpace='nowrap'>
                {locationDeliveryCost === 0 ? t('FREE') : formatMoney(locationDeliveryCost)}
              </RowText>
            </RowContainer>
            <RowContainer>
              <RowText color={colorConstants.white} paddingRight={paddingSizes.medium}>
                {t('Peak Period Surcharge')}
                <br/>
                <SmallText normalised color='white'>
                  {t('Charged by our logistics partner to ensure high availability of drivers for your order')}
                </SmallText>
              </RowText>
              <RowText color={colorConstants.gray} whiteSpace='nowrap'>
                {formatMoney(surchargeDeliveryCost)}
              </RowText>
            </RowContainer>
          </StyledTooltipContainer>
        )
      }
    </>
  ) : null
}

interface StoreOrderSummaryProps {
  isAppointmentReady?: boolean
  storeOrder: Oms.V4.StoreOrder
  voucherSummary?: {
    voucherTitle: string
    redeemedAmount: number
  }
  shouldShowTotal: boolean
  shouldShowVoucherSummary: boolean
  shouldShowSurchargeWarning: boolean
  payableText?: string
  loading?: boolean
}

const StoreOrderSummary = ({
  isAppointmentReady,
  storeOrder,
  voucherSummary,
  shouldShowTotal = true,
  shouldShowVoucherSummary = false,
  shouldShowSurchargeWarning = false,
  payableText,
  loading = false
}: StoreOrderSummaryProps): JSX.Element => {
  const {
    reachedMinOrder,
    amountToMinOrder,
    deliveryPromotions,
    otherPromotions,
    minimumOrderOverridePromotions,
    beforeDeliveryCharges,
    otherCharges,
    deliveryCost,
    paymentAmount,
    surchargeDeliveryCost,
    locationDeliveryCost
  } = useMemo(() => analyseStoreOrder(storeOrder), [storeOrder])

  const { t } = React.useContext(TranslationContext)
  const { formatMoney } = useAppContext()
  const { voucherTitle, redeemedAmount } = voucherSummary || {}
  return (
    <React.Fragment>
      <Div paddingTop={paddingSizes.small} paddingBottom={paddingSizes.small}>
        <Row
          bold
          leftText={t('Subtotal')}
          rightText={formatMoney(loading ? 0 : storeOrder.subtotal)}
        />
        {!loading && !reachedMinOrder && shouldShowSurchargeWarning && (
          <React.Fragment>
            {minimumOrderOverridePromotions.map(
              (promotion: { name: string; amount: number }) => (
                <Row leftText={promotion.name} />
              )
            )}
            <Row
              color={colorConstants.red}
              leftText={t('Surcharge for orders under _min_', {
                min: formatMoney(storeOrder.minimumOrder)
              })}
              rightText={formatMoney(amountToMinOrder)}
            />
          </React.Fragment>
        )}
        {!loading && otherPromotions.length > 0 &&
          otherPromotions.map(
            (promotion: { id: string; name: string; amount: number }) => (
              <Promotion key={promotion.id} promotion={promotion} />
            )
          )}
        {!loading && beforeDeliveryCharges.length > 0 &&
          beforeDeliveryCharges.map((charge) => (
            <Charge key={charge.id} charge={charge} />
          ))}
        <DeliveryFee
          deliveryCost={loading ? 0 : deliveryCost}
          isAppointmentReady={isAppointmentReady}
          storeOrder={storeOrder}
          surchargeDeliveryCost={surchargeDeliveryCost}
          locationDeliveryCost={locationDeliveryCost}
        />
        {!loading && deliveryPromotions.length > 0 &&
          deliveryPromotions.map((promotion) => (
            <Promotion key={promotion.id} promotion={promotion} />
          ))}
        {!loading && otherCharges.length > 0 &&
          otherCharges.map((charge) => (
            <Charge key={charge.id} charge={charge} />
          ))}
        {storeOrder.roundingAdjustment !== 0 && (
          <Row
            leftText={t('Rounding Adjustment')}
            rightText={formatMoney(loading ? 0 : storeOrder.roundingAdjustment)}
          />
        )}
      </Div>
      {shouldShowTotal && (
        <React.Fragment>
          <Divider />
          <Div>
            <Total normalised total={loading ? 0 : storeOrder.total} />
          </Div>
        </React.Fragment>
      )}
      {shouldShowVoucherSummary &&
        !isEmpty(voucherTitle) &&
        !isNaN(redeemedAmount) && (
          <React.Fragment>
            {
              !loading && <Div marginBottom={marginSizes.small}>
                <Row
                  leftText={t('Voucher | _voucherTitle_', { voucherTitle })}
                  rightText={formatMoney(-1 * redeemedAmount)}
                />
              </Div>
            }
            <PayableContainer>
              <Row
                bold
                Text={PayableHeader}
                leftText={payableText || t('Total Payable')}
                rightText={loading ? '0' : formatMoney(
                  !isNaN(paymentAmount)
                    ? paymentAmount
                    : Math.max(0, storeOrder.total - redeemedAmount)
                )}
              />
            </PayableContainer>
          </React.Fragment>
        )}
    </React.Fragment>
  )
}

export default StoreOrderSummary
